/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { Debug, notImplemented, release, isNullOrUndefined, matchRedirect } from '@awayfl/swf-loader';
import { alCoerceString, alDefineObjectProperties, alToString } from '../runtime';
import { avm1BroadcastEvent } from './AVM1Utils';
import { URLLoaderEvent as Event, URLLoader, URLRequest, URLVariables } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
export function loadAVM1DataObject(context, url, method, contentType, data, target) {
    var request = new URLRequest(url);
    var directUrl = request.url || '';
    var cleanUrl = directUrl.replace(/\?.*$/, '');
    var redirect = matchRedirect(directUrl, null);
    if (redirect) {
        if (redirect.supressLoad) {
            console.log('[LOADER] Load supressed ', redirect.url);
            return;
        }
        console.log('[LOADER] Override loading url:', redirect.url);
        request.url = redirect.url;
    }
    else {
        console.log('[LOADER] start loading the url:', cleanUrl);
    }
    if (method) {
        request.method = method;
    }
    if (contentType) {
        //request.contentType = contentType;
    }
    if (data) {
        release || Debug.assert(typeof data === 'string');
        // generate valid payload
        request.data = contentType === 'application/x-www-form-urlencoded'
            ? new URLVariables(data)
            : data;
    }
    var loader = new URLLoader();
    loader.dataFormat = 'text'; // flash.net.URLLoaderDataFormat.TEXT;
    var completeHandler = function (event) {
        loader.removeEventListener(Event.LOAD_COMPLETE, completeHandler);
        release || Debug.assert(typeof loader.data === 'string');
        avm1BroadcastEvent(context, target, 'onData', [loader.data]);
    };
    loader.addEventListener(Event.LOAD_COMPLETE, completeHandler);
    target._as3Loader = loader;
    if (redirect && redirect.supressErrors) {
        loader.addEventListener(Event.LOAD_ERROR, function (event) {
            console.log('[LOADER] Error supressed by redirect rule as empty complete events!', event);
            loader.dispatchEvent(new Event(Event.LOAD_COMPLETE, loader));
        });
    }
    loader.load(request);
}
var AVM1LoadVarsFunction = /** @class */ (function (_super) {
    __extends(AVM1LoadVarsFunction, _super);
    function AVM1LoadVarsFunction(context) {
        var _this = _super.call(this, context) || this;
        _this.alSetOwnPrototypeProperty(new AVM1LoadVarsPrototype(context, _this));
        return _this;
    }
    AVM1LoadVarsFunction.prototype.alConstruct = function (args) {
        var obj = new AVM1Object(this.context);
        obj.alPrototype = this.alGetPrototypeProperty();
        obj.isAVM1DataObject = true;
        return obj;
    };
    AVM1LoadVarsFunction.prototype.alCall = function (thisArg, args) {
        return this.alConstruct(args);
    };
    return AVM1LoadVarsFunction;
}(AVM1Function));
export { AVM1LoadVarsFunction };
var AVM1LoadVarsPrototype = /** @class */ (function (_super) {
    __extends(AVM1LoadVarsPrototype, _super);
    function AVM1LoadVarsPrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            toString: {
                value: _this._toString
            },
            load: {
                value: _this.load
            },
            onData: {
                value: _this.defaultOnData
            },
            decode: {
                value: _this.decode
            },
            send: {
                value: _this.load
            },
            sendAndLoad: {
                value: _this.sendAndLoad
            }
        });
        return _this;
    }
    AVM1LoadVarsPrototype.prototype.getBytesLoaded = function () {
        if (!this._as3Loader) {
            return undefined;
        }
        return this._as3Loader.bytesLoaded;
    };
    AVM1LoadVarsPrototype.prototype.getBytesTotal = function () {
        if (!this._as3Loader) {
            return undefined;
        }
        return this._as3Loader.bytesTotal;
    };
    AVM1LoadVarsPrototype.prototype.load = function (url) {
        url = alCoerceString(this.context, url);
        if (!url) {
            return false;
        }
        loadAVM1DataObject(this.context, url, null, null, null, this);
        return true;
    };
    AVM1LoadVarsPrototype.prototype.defaultOnData = function (src) {
        if (isNullOrUndefined(src)) {
            avm1BroadcastEvent(this.context, this, 'onLoad', [false]);
            return;
        }
        AVM1LoadVarsPrototype.prototype.decode.call(this, src);
        this.alPut('loaded', true);
        avm1BroadcastEvent(this.context, this, 'onLoad', [true]);
    };
    AVM1LoadVarsPrototype.prototype.decode = function (queryString) {
        queryString = alCoerceString(this.context, queryString);
        var vars = new URLVariables(queryString);
        for (var key in vars.variables) {
            this.alPut(key, vars.variables[key]);
        }
    };
    AVM1LoadVarsPrototype.prototype._toString = function () {
        var payload = {};
        for (var key in this._ownProperties) {
            payload[key] = this.alGet(key);
        }
        return (new URLSearchParams(payload)).toString();
    };
    AVM1LoadVarsPrototype.prototype.send = function (url, target, method) {
        url = alCoerceString(this.context, url);
        method = isNullOrUndefined(method) ? 'POST' : alCoerceString(this.context, method);
        notImplemented('AVM1LoadVarsPrototype.send');
        return false;
    };
    AVM1LoadVarsPrototype.prototype.sendAndLoad = function (url, target, method) {
        url = alCoerceString(this.context, url);
        method = isNullOrUndefined(method) ? 'POST' : alCoerceString(this.context, method);
        if (!url || !(target instanceof AVM1Object)) {
            return false;
        }
        if (!target.isAVM1DataObject) {
            return false;
        }
        var contentType = this.alGet('contentType');
        contentType = isNullOrUndefined(contentType) ?
            'application/x-www-form-urlencoded' :
            alCoerceString(this.context, contentType);
        var data = alToString(this.context, this);
        loadAVM1DataObject(this.context, url, method, contentType, data, target);
        return true;
    };
    return AVM1LoadVarsPrototype;
}(AVM1Object));
export { AVM1LoadVarsPrototype };
